import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../../constants/svgIcon';
import { getWebBoosterVairant } from '../../../../constants/webBoosterType';
import { TtsLocale } from '../../../../models/Base';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../../../models/DraftStore';
import { WebBoosterType } from '../../../../models/UserStore';
import { AppBar, SvgIcon, Switch, TextArea } from '../../../Molecules';

import styles from './CreateMemoryGameContentEditTemplate.module.css';

type TextInputProps = {
  value: string;
  placeHolder?: string;
  missingStrokes: string[];
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
};

type CreateMemoryGameContentEditTemplateProps = {
  type: QuickCreateGameMode | undefined;
  webBoosterType: WebBoosterType | undefined;
  mode: 'Question' | 'Answer' | undefined;
  selectedLanguage: TtsLocale;
  language: string;
  isUseTts: boolean;
  soundText: string;
  gameContent: TextInputProps;
  textLength: number;
  maxTextLength: number;
  hasAutoTranslate: boolean;
  isAutoTranslateDisabled: boolean;
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  errorMessage: string;
  onClickLanguage: () => void;
  onSwitchTts: () => void;
  onClickAutoTranslate: () => void;
  onClickResetTranslate: () => void;
  onCopyScriptures: () => void;
  onClickAudioBar: () => void;
  onClickMissingStrokeData: () => void;
  onClickPaste: () => void;
  onClickScan: () => void;
  onClickOK: () => void;
  onClickBack: () => void;
};

export const CreateMemoryGameContentEditTemplate = (
  props: CreateMemoryGameContentEditTemplateProps,
) => {
  const {
    type,
    webBoosterType,
    mode,
    selectedLanguage,
    language,
    isUseTts,
    soundText,
    gameContent,
    textLength,
    maxTextLength,
    hasAutoTranslate,
    isAutoTranslateDisabled,
    isNextDisabled,
    isExceedLimit,
    errorMessage,
    onClickLanguage,
    onSwitchTts,
    onClickAutoTranslate,
    onClickResetTranslate,
    onCopyScriptures,
    onClickAudioBar,
    onClickMissingStrokeData,
    onClickPaste,
    onClickScan,
    onClickOK,
    onClickBack,
  } = props;
  const { t } = useTranslation();

  return (
    <div className={styles.createMemoryContentEdit}>
      <AppBar
        title={
          type ? t(getQuickCreateGameModeProps(type, webBoosterType).name) : ''
        }
        onClickBack={onClickBack}
      />
      <div className={styles.languageSelector} onClick={onClickLanguage}>
        <SvgIcon
          name={
            mode === 'Question' ? SVGIconType.LanguageQ : SVGIconType.LanguageA
          }
          width={28}
          height={28}
          color={'white'}
        />
        <span className={styles.language}>{t(language)}</span>
        <SvgIcon
          name={SVGIconType.Triangle}
          width={20}
          height={20}
          color={'white'}
        />
      </div>
      <div className={styles.container}>
        {/* <p className={styles.title}>{t(title)}</p> */}

        <div className={styles.buttonContainer}>
          {mode === 'Question' ? (
            <div className={styles.textToSpeech}>
              <SvgIcon name={SVGIconType.TTS} width={28} height={28} />
              <span>{t('+Voice (Text-to-Speech)')}</span>
              <div className={styles.switch}>
                <Switch
                  checked={isUseTts}
                  onChange={onSwitchTts}
                  label={isUseTts ? t('ON') : t('OFF')}
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className={styles.cellHeader}>
          <div className={styles.cellHeaderTitle}>
            <SvgIcon
              name={
                mode === 'Question'
                  ? SVGIconType.LanguageQ
                  : SVGIconType.LanguageA
              }
              width={24}
              height={24}
            />
            {mode === 'Question'
              ? t(mode)
              : mode === 'Answer'
              ? t(
                  `${mode} (${
                    getWebBoosterVairant(webBoosterType).shortCreateName
                  })`,
                )
              : ''}
          </div>
          {mode === 'Question' ? (
            hasAutoTranslate ? (
              <div
                className={`${styles.translateButton}`}
                onClick={onClickResetTranslate}
              >
                {t('Reset')}
                <SvgIcon name={SVGIconType.Retry} width={24} height={24} />
              </div>
            ) : (
              <div
                className={`${styles.translateButton} ${
                  isAutoTranslateDisabled ? styles.translateDisabled : ''
                }`}
                onClick={onClickAutoTranslate}
              >
                {t('Translation of Answer')}
                <SvgIcon
                  name={SVGIconType.Translation}
                  width={24}
                  height={24}
                />
              </div>
            )
          ) : type &&
            getQuickCreateGameModeProps(type, webBoosterType)
              .hasCopyScriptures ? (
            <div
              className={`${styles.translateButton}`}
              onClick={onCopyScriptures}
            >
              {t('Copy Scriptures')}
              <SvgIcon name={SVGIconType.BibleLine} width={24} height={24} />
            </div>
          ) : null}
        </div>

        <div className={styles.textSection}>
          <div className={styles.ttsTextArea}>
            {isUseTts && mode === 'Question' ? (
              <div className={styles.audioBar} onClick={onClickAudioBar}>
                <SvgIcon
                  name={SVGIconType.PlayerPlay}
                  width={35}
                  height={35}
                  color={'#808080'}
                />
                <div className={styles.trackTextContainer}>
                  <div className={styles.audioTrack} />
                  <div className={styles.ttsTextContainer}>
                    {'['}
                    <span className={styles.ttsText}>{soundText || '---'}</span>
                    {']'}
                  </div>
                </div>
              </div>
            ) : null}
            <TextArea
              textEntryClassName={`${styles.textEntry} ${
                errorMessage ? styles.textEntryError : ''
              }`}
              textEntryAreaClassName={styles.textEntryArea}
              value={gameContent.value}
              onChange={gameContent.onChange}
              isAutoResize={true}
              placeholder={
                gameContent.placeHolder
                  ? t(gameContent.placeHolder, { lng: selectedLanguage })
                  : t('Enter text', { lng: selectedLanguage })
              }
            />
            <div className={styles.pasteScanContainer}>
              <div
                className={styles.textActionContainer}
                onClick={onClickPaste}
              >
                <SvgIcon name={SVGIconType.Copy} width={24} height={24} />
                {t('Paste')}
              </div>
              <div className={styles.textActionContainer} onClick={onClickScan}>
                <SvgIcon name={SVGIconType.OCRScan} width={24} height={24} />
                {t('Scan')}
              </div>
            </div>
          </div>
          <span
            className={styles.charLimit}
            style={{ color: isExceedLimit ? '#D95141' : '#808080' }}
          >
            {t('{count} words (max. {maxCount})', {
              count: textLength,
              maxCount: maxTextLength,
            })}
          </span>
          {gameContent.missingStrokes.length > 0 ? (
            <div
              className={styles.missingStrokeData}
              onClick={onClickMissingStrokeData}
            >
              <p>{t('Stroke data not available:')}</p>
              <p>{gameContent.missingStrokes.join(' ')}</p>
              <SvgIcon name={SVGIconType.Arrow} width={16} height={16} />
            </div>
          ) : null}
          <div className={styles.errorMessage}>
            <p>{t(errorMessage)}</p>
          </div>
        </div>
      </div>
      <div className={styles.bottomBar}>
        <button
          className={styles.okButton}
          onClick={onClickOK}
          disabled={isNextDisabled}
        >
          <span className={styles.okText}>{t('OK')}</span>
          {isNextDisabled ? <div className={styles.blackShadow} /> : null}
        </button>
      </div>
    </div>
  );
};
